import { IConfigExtended, IXhrConfig } from './IHlsJsExtended';
import { XhrWorkerEvent } from './workers/XhrWorkerEvent';
// // @ts-ignore
// import XhrWorker from './XhrWorker.vbworker.ts';
import type { FragmentLoaderContext, Loader, LoaderContext, LoaderStats } from 'hls.js';
import { VbLoadStats } from './XhrWorker.Contract';
import { PlayerRuntimeConfig } from '../player/VbPlayerRuntimeConfig';

/**
 * XHR loader fed into the hls.js config which handles the main thread side of things,
 * basically bridging the API to the web worker side through messages.
 *
 * There is a single worker associated with an HlsPlayer instance.
 */
export class XhrWorkerLoader implements Loader<FragmentLoaderContext> {
	public static workerRegistry: Record<string, Worker> = {};

	private callbacks;
	public context!: FragmentLoaderContext;
	private isDestroyed: boolean;
	private trequest: number;
	private worker: Worker;
	private xhrConfig: IXhrConfig;
	private key: string;
	public stats: LoaderStats;

	constructor (config: IConfigExtended) {
		this.xhrConfig = config.xhrConfig;
		this.key = config.playerId + '@' + config.fragType;

		this.worker = XhrWorkerLoader.workerRegistry[this.key] ||
			(XhrWorkerLoader.workerRegistry[this.key] = new Worker(new URL('XhrWorker.vbworker.js', PlayerRuntimeConfig.workerBaseUrl), { type: 'module' }));
		this.stats = new VbLoadStats();
		this.worker.onmessage = this.onMessage;
	}

	public static onPlayerDestroy(playerId: string): void {
		Object.keys(XhrWorkerLoader.workerRegistry)
			.forEach(key => {
				if (key?.includes(playerId)) {
					const worker = XhrWorkerLoader.workerRegistry[key];
					if (worker) {
						worker.postMessage({ type: XhrWorkerEvent.DESTROY });
						worker.terminate();

						delete XhrWorkerLoader.workerRegistry[key];
					}
				}
			});
	}

	public destroy(): void {
		if (this.isDestroyed) {
			return;
		}

		this.isDestroyed = true;
		this.worker.postMessage({ type: XhrWorkerEvent.DESTROY });
		this.worker.onmessage = undefined;

		this.callbacks =
			this.worker =
				undefined;
	}

	public abort(): void {
		if (this.isDestroyed) {
			return;
		}

		this.worker.postMessage({ type: XhrWorkerEvent.ABORT });
		this.destroy();
	}

	public load(context, config, callbacks): void {
		this.callbacks = callbacks;
		this.context = context;
		this.trequest = performance.now();

		const { rangeEnd, rangeStart, responseType, url, level, type } = context;

		const simplifiedContext = {
			rangeEnd,
			rangeStart,
			responseType,
			url,
			level, // playlists
			type
		};

		this.worker.postMessage({
			config,
			context: simplifiedContext,
			type: XhrWorkerEvent.LOAD,
			xhrConfig: this.xhrConfig
		});
	}

	private onMessage = ({ data }) => {
		if (this.isDestroyed) {
			return;
		}

		switch (data.type) {
			case XhrWorkerEvent.ERROR:
				this.callbacks.onError(data.response, this.context, data.networkDetails);
				this.destroy();
				break;

			case XhrWorkerEvent.PROGRESS:
				if (this.callbacks.onProgress) {
					data.stats.loading = this.getLoadingStats(data.stats);
					this.callbacks.onProgress(data.stats, this.getUpdatedContext(data.stats), data.data, data.xhr);
				}
				break;

			case XhrWorkerEvent.SUCCESS:
				data.stats.loading = this.getLoadingStats(data.stats);
				this.callbacks.onSuccess(data.response, data.stats, this.getUpdatedContext(data.stats));

				break;

			case XhrWorkerEvent.TIMEOUT:
				this.callbacks.onTimeout(data.response, this.context, data.networkDetails);
				this.destroy();
				break;
		}
	};

	private getUpdatedContext(stats: VbLoadStats): LoaderContext {
		const context = { ...this.context };

		if (stats && this.context?.frag) {
			stats.loading = this.getLoadingStats(stats);
			this.context.frag.stats = stats;
		}

		return this.context;
	}

	private getLoadingStats(stats: VbLoadStats): { start: number, first: number, end: number } {
		return {
			start: this.trequest,
			end: this.trequest + (stats.loading.end - stats.loading.start),
			first: this.trequest + (stats.loading.first - stats.loading.start)
		};
	}

}
